import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { VStack, Box, Button, CircularProgress, HStack, Heading } from "@chakra-ui/react";

import {
  userAgentIsMobile,
  userAgentIsIOS,
  shouldOpenInSafari,
  getBrowser,
  Browser,
} from "utils/device";
import StepsBox from "components/StepsBox";
import { fetchProvider, fetchFeatureFlagsFromServer } from "utils/backend";
import {
  extractDomain,
  extractVariablesFromResponseMatches,
  isThreeJanApp,
  isSophonApp,
} from "utils/helper";
import { updateSession } from "utils/update-session";
import { Text, Image } from "@chakra-ui/react";
import { BraveWarningModal } from "components/BraveWarningModal";
import SharedDataBox from "components/SharedDataBox";
import TermsAndPrivacy from "components/TermsAndPrivacy";
import { sendLogs } from "utils/logs";

// Chromium legacy browsers (< Chromium 91.0) do not handle external protocols: https://github.com/blueboxd/chromium-legacy/issues/18

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Verifier: React.FC = () => {
  const query = useQuery();
  const APP_ID = import.meta.env.VITE_PUBLIC_APP_ID || "";
  const APP_SECRET = import.meta.env.VITE_PUBLIC_APP_SECRET || "";

  const [appName, setAppName] = useState<string>("");
  const [appImage, setAppImage] = useState<string>("");
  const [appId, setAppId] = useState<string>("");
  const [variables, setVariables] = useState<string[]>([""]);
  const [providerHost, setProviderHost] = useState<string>("");
  const [providerLogo, setProviderLogo] = useState<string>("");
  const [updatedSession, setUpdatedSession] = useState<boolean>(false);
  const [steps, setSteps] = useState<string[]>([]);
  const [isBraveWarningModalOpen, setIsBraveWarningModalOpen] = useState(false);
  const openInSafari = shouldOpenInSafari();

  const isNewLinkingEnabled = async () => {
    try {
      const template = query.get("template");
      if (!template) {
        return false;
      }

      const templateObj = JSON.parse(template);
      const featureFlags = await fetchFeatureFlagsFromServer({
        featureFlagNames: ["newLinkingAndroid"],
        appId: templateObj?.applicationId,
        providerId: templateObj?.providerId,
        sessionId: templateObj?.sessionId,
      });

      return featureFlags.newLinkingAndroid === true;
    } catch (error) {
      console.error("Error checking feature flag:", error);
      return false;
    }
  };

  // Check feature flag on Android and redirect if enabled
  useEffect(() => {
    const checkAndRedirect = async () => {
      if (!userAgentIsIOS && userAgentIsMobile) {
        const enabled = await isNewLinkingEnabled();
        if (enabled) {
          const currentUrl = window.location.href;
          const linkUrl = currentUrl.replace("/verifier", "/link");
          window.location.href = linkUrl;
        }
      }
    };

    checkAndRedirect();
  }, [query]);

  // Open in Safari if not Safari and on iOS
  useEffect(() => {
    if (openInSafari) {
      window.open(`x-safari-${window.location.href}`, "_blank");
    }
  }, []);

  function launchApp(requestUrl: string, deepLink: string) {
    let appInstalled = false;
    let timeoutId: string | number | NodeJS.Timeout | undefined;

    // Create hidden iframe to test deep link
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.style.width = "1px";
    iframe.style.height = "1px";
    document.body.appendChild(iframe);

    // Function to clean up
    const cleanup = () => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    // If page becomes hidden, app opened successfully
    const onVisibilityChange = () => {
      if (document.hidden) {
        appInstalled = true;
        cleanup();
        // Open in main window since app is installed
        window.location.href = deepLink;
      }
    };

    // Listen for visibility change
    document.addEventListener("visibilitychange", onVisibilityChange, { once: true });

    // Test deep link in iframe
    iframe.src = deepLink;

    // After timeout, assume app not installed
    timeoutId = setTimeout(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      cleanup();

      if (!appInstalled) {
        // App not installed - go directly to instant app URL
        // Use a fresh window context to avoid popup
        window.location.href = requestUrl;
      }
    }, 1500);
  }

  const handleContinueOnAppClipClick = () => {
    if (userAgentIsIOS && getBrowser() === Browser.Brave) {
      setIsBraveWarningModalOpen(true);
    } else {
      continueOnAppClip();
    }
  };

  const getSessionContext = () => {
    try {
      const template = query.get("template");
      const templateObj = template ? JSON.parse(template) : null;
      return {
        sessionId: templateObj?.sessionId || '',
        providerId: templateObj?.providerId || '',
        appId: templateObj?.applicationId || '',
      };
    } catch {
      return { sessionId: '', providerId: '', appId: '' };
    }
  };

  const logSharePageInteraction = (interactionType: string) => {
    const context = getSessionContext();
    sendLogs(interactionType, {
      ...context,
      logLevel: 'INFO',
    });
  };

  const continueOnAppClip = () => {
    const template = query.get("template");
    if (!template) return;
    logSharePageInteraction("CONTINUE_ON_APP_CLIP_VERIFIER_SCREEN");
    const url = encodeURIComponent(template);
    const encodedUrl = url.replace(/[()]/g, (match) => {
      return "%" + match.charCodeAt(0).toString(16).toUpperCase();
    });
    if (userAgentIsIOS) {
      window.open(
        `https://appclip.apple.com/id?p=org.reclaimprotocol.app.clip&template=${encodedUrl}`,
        "_blank"
      );
      if (!isThreeJanApp(appId)) {
        window.location.href = `https://share.reclaimprotocol.org/verify/?template=${encodedUrl}`;
      }
    } else {
      // ANDROID flow
      const instantAppUrl = `https://share.reclaimprotocol.org/verify/?template=${encodedUrl}`;
      // Construct Android intent deep link
      const deepLink = `intent://details?id=org.reclaimprotocol.app&launch=true&url=${encodeURIComponent(
        instantAppUrl
      )}&template=${encodedUrl}#Intent;scheme=market;action=android.intent.action.VIEW;package=com.android.vending;end;`;

      try {
        launchApp(instantAppUrl, deepLink);
      } catch (e) {
        // Final fallback → verifier
        window.location.href = `https://share.reclaimprotocol.org/verifier/?template=${encodedUrl}`;
      }
    }
  };

  const getAppInfo = async (appId: string) => {
    try {
      const response = await fetch(
        `https://api.reclaimprotocol.org/api/applications/info/${appId}`
      );
      const data = await response.json();
      const appName = data.application.name;
      const appImage = data.application?.appImageUrl ?? null;
      setAppName(appName);
      setAppImage(appImage);
    } catch (error) {
      console.error("Error fetching app name:", error);
    }
  };

  const fetchProviderData = async (providerId: string, providerVersion?: string) => {
    const provider = await fetchProvider(providerId, APP_ID, APP_SECRET, providerVersion);
    const url = provider?.providers.httpProvider[0].loginUrl;
    const providerLogo = provider?.providers.httpProvider[0].logoUrl;
    const domain = extractDomain(url);
    const variables = extractVariablesFromResponseMatches(
      provider?.providers?.httpProvider[0]?.requestData[0]?.responseMatches ?? []
    );
    setVariables(variables);
    setProviderHost(domain);
    setProviderLogo(providerLogo);
    if (provider?.providers.httpProvider[0].stepsToFollow) {
      setSteps(
        provider?.providers.httpProvider[0].stepsToFollow
          .split("\n")
          .map((step: string) => step.trim())
      );
    }
  };

  useEffect(() => {
    try {
      const template = query.get("template");
      const templateObj = template ? JSON.parse(template) : null;
      const applicationId = templateObj?.applicationId;
      const providerId = templateObj?.providerId;
      const providerVersion = templateObj?.providerVersion;
      // nonSafariRedirect();
      getAppInfo(applicationId);
      fetchProviderData(providerId, providerVersion);
      setAppId(applicationId.trim());
    } catch (e) {
      console.error("Error parsing template", e);
    }
  }, []);

  useEffect(() => {
    if (updatedSession) {
      return;
    }
    const template = query.get("template");
    if (!template) return;
    const templateObj = template ? JSON.parse(template) : null;
    const sessionId = templateObj?.sessionId;
    updateSession(sessionId, "USER_INIT_VERIFICATION").then((data) => {
      if (data) {
        setUpdatedSession(true);
      }
    });
  }, [query, updatedSession]);

  return (
    <>
      <VStack mx="auto" spacing={5} justify={userAgentIsMobile ? "space-between" : "center"}>
        {appName !== "" && providerHost !== "" ? (
          <>
            <Box height="10px" />
            {providerLogo && appImage && (
              <Box
                maxWidth="400px"
                width="calc(100% - 24px)"
                mt={5}
                display="flex"
                justifyContent="left"
              >
                <Box
                  w={"62px"}
                  height={"62px"}
                  backgroundImage={appImage}
                  backgroundSize={"cover"}
                  backgroundPosition={"center"}
                  borderRadius={"15px"}
                  border={"5px solid #fff"}
                  zIndex={1}
                />
                <Box
                  w={"61px"}
                  height={"61px"}
                  backgroundImage={providerLogo}
                  backgroundSize={"cover"}
                  backgroundPosition={"center"}
                  borderRadius={"15px"}
                  zIndex={0}
                  marginLeft="-15px"
                />
              </Box>
            )}
            <Box
              maxWidth="400px"
              width="calc(100% - 24px)"
              pr={10}
              color="#0F172A"
              lineHeight="27.45px"
              fontSize="23px"
              fontWeight={500}
            >
              <strong>{appName}</strong> is requesting data from{" "}
              <span style={{ textTransform: "capitalize" }}>{providerHost}</span>
            </Box>
            <StepsBox host={providerHost} variables={variables} steps={steps} appId={appId} />
            {isSophonApp(appId) && variables.length > 0 && <SharedDataBox variables={variables} />}
            <VStack
              bg="#F2F2F7"
              px={6}
              pt={4}
              pb={4}
              borderRadius={16}
              maxWidth="400px"
              width="calc(100% - 24px)"
              spacing={2}
              left="50%"
              right="12px"
            >
              <HStack width="100%">
                <Image
                  src="/icons/privacy.svg"
                  alt="steps"
                  objectFit="contain"
                  borderRadius="16px"
                />
                <Heading
                  as="h2"
                  fontSize="18px"
                  lineHeight="19px"
                  fontWeight={500}
                  color={"#1D2126"}
                >
                  Privacy
                </Heading>
              </HStack>

              <Box w={"100%"}>
                <Text
                  color="gray.600"
                  fontSize="16px"
                  lineHeight="19px"
                  fontWeight={400}
                  width="100%"
                  my={2}
                >
                  {isSophonApp(appId)
                    ? "Your login credentials stay secure"
                    : `Your login credentials and private data stay secure and are NEVER visible with ${appName}.`}
                </Text>
              </Box>
            </VStack>
          </>
        ) : (
          <CircularProgress color="#007AFF" isIndeterminate pt={10} />
        )}
        {userAgentIsMobile && openInSafari ? (
          <Button
            maxWidth="400px"
            width="calc(100% - 24px)"
            bg="#007AFF"
            borderRadius="12px"
            height="50px"
            textColor="white"
            lineHeight="22px"
            fontSize="17px"
            fontWeight={400}
            onClick={() => window.open(`x-safari-${window.location.href}`, "_blank")}
            _active={{ bg: "#007AFF" }}
            _hover={{ bg: "#007AFF" }}
          >
            Open in Safari
          </Button>
        ) : (
          <Button
            maxWidth="400px"
            width="calc(100% - 24px)"
            bg="#007AFF"
            borderRadius="12px"
            height="50px"
            textColor="white"
            lineHeight="22px"
            fontSize="17px"
            fontWeight={400}
            onClick={handleContinueOnAppClipClick}
            _active={{ bg: "#007AFF" }}
            _hover={{ bg: "#007AFF" }}
          >
            Continue to verify
          </Button>
        )}

        <BraveWarningModal
          isOpen={isBraveWarningModalOpen}
          onClose={() => setIsBraveWarningModalOpen(false)}
          onContinueToAppClip={continueOnAppClip}
        />

        {isSophonApp(appId) && <TermsAndPrivacy />}
      </VStack>
    </>
  );
};

export default Verifier;
